import { getIntlLocale } from '@/lib/language';
import { formatRelativeTime } from '@/lib/utils';
import _ from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  ChainlitContext,
  IThread,
  useChatInteract
} from '@chainlit/react-client';

import { Loader } from '@/components/Loader';
import MaterialIcon from '@/components/MaterialIcon';

interface SearchChatsProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function SearchChats({
  open: openProp,
  onOpenChange
}: SearchChatsProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { clear } = useChatInteract();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = useCallback(
    (value: boolean) => {
      onOpenChange?.(value);
      setInternalOpen(value);
    },
    [onOpenChange]
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [threads, setThreads] = useState<IThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const apiClient = useContext(ChainlitContext);

  const debouncedSearch = useMemo(
    () =>
      _.debounce(async (query: string) => {
        setLoading(true);
        try {
          const { data } = await apiClient.listThreads(
            { first: 24, cursor: undefined },
            { search: query || undefined }
          );
          setThreads(data || []);
        } catch (error) {
          toast.error(
            `${t('threadHistory.sidebar.errors.fetch')}: ${String(error)}`
          );
        } finally {
          setLoading(false);
        }
      }, 300),
    [apiClient, t]
  );

  const groupedThreads = useMemo(() => {
    return _.groupBy(threads, (thread) => {
      const date = new Date(thread.createdAt);
      const now = new Date();
      const startOfToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
      const startOfYesterday = new Date(startOfToday);
      startOfYesterday.setDate(startOfYesterday.getDate() - 1);
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfWeek.getDate() - 7);

      if (date >= startOfToday)
        return t('threadHistory.sidebar.timeframes.today', 'Today');
      if (date >= startOfYesterday)
        return t('threadHistory.sidebar.timeframes.yesterday', 'Yesterday');
      if (date >= startOfWeek)
        return t(
          'threadHistory.sidebar.timeframes.previous7days',
          'Previous 7 days'
        );
      return `${date.toLocaleString(getIntlLocale(i18n.language), {
        month: 'long'
      })} ${date.getFullYear()}`;
    });
  }, [threads, t, i18n.language]);

  const selectedThread = threads.find((thread) => thread.id === selectedId);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, setOpen]);

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSelectedId(null);
      return;
    }
    debouncedSearch(searchQuery);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch, open]);

  const openThread = (threadId: string) => {
    setOpen(false);
    navigate(`/thread/${threadId}`);
  };

  const startNewChat = () => {
    setOpen(false);
    clear();
    navigate('/');
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-10 bg-black/70 backdrop-blur-md animate-backdrop-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('threadHistory.sidebar.filters.search', 'Search')}
        className="fongpt-search-modal w-full max-w-5xl h-[min(72vh,720px)] rounded-[24px] overflow-hidden flex flex-col animate-panel-in"
      >
        <header className="flex h-[72px] items-center gap-3 px-5 md:px-6 border-b border-white/[0.06]">
          <MaterialIcon name="search" className="text-muted text-[22px]" />
          <input
            autoFocus
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t(
              'threadHistory.sidebar.filters.placeholder',
              'Search conversations'
            )}
            className="flex-1 bg-transparent outline-none text-[20px] tracking-[-0.02em] text-primary placeholder:text-primary/35"
          />
          {searchQuery ? (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="fongpt-hover w-8 h-8 rounded-full flex items-center justify-center text-muted cursor-pointer"
              aria-label={t('common.actions.clearSearch')}
            >
              <MaterialIcon name="cancel" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="fongpt-hover flex h-8 w-8 items-center justify-center rounded-full text-muted cursor-pointer"
            aria-label={t('common.actions.closeSearch', 'Close search')}
          >
            <MaterialIcon name="close" />
          </button>
        </header>

        <div className="flex flex-1 min-h-0">
          <div className="w-full md:w-[42%] border-r border-white/[0.06] overflow-y-auto">
            <div className="px-3 pt-4 pb-2">
              <p className="px-3 pb-2 text-[11px] uppercase tracking-[0.16em] text-muted">
                {t('threadHistory.sidebar.actions.title', 'Actions')}
              </p>
              <button
                onClick={startNewChat}
                className="fongpt-hover w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-primary cursor-pointer"
              >
                <MaterialIcon name="edit" />
                <span className="text-sm font-medium">
                  {t(
                    'threadHistory.sidebar.actions.newChat',
                    'Start a new conversation'
                  )}
                </span>
              </button>
            </div>

            {loading ? (
              <div className="p-8 flex items-center justify-center">
                <Loader />
              </div>
            ) : Object.keys(groupedThreads).length === 0 ? (
              <p className="px-6 py-8 text-sm text-secondary">
                {t('threadHistory.sidebar.empty', 'No conversations found')}
              </p>
            ) : (
              Object.entries(groupedThreads).map(([group, monthThreads]) => (
                <div key={group} className="px-3 pb-3">
                  <p className="px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-muted">
                    {group}
                  </p>
                  {monthThreads.map((thread) => {
                    const isSelected = selectedId === thread.id;
                    return (
                      <button
                        key={thread.id}
                        onClick={() => setSelectedId(thread.id)}
                        onDoubleClick={() => openThread(thread.id)}
                        className={`fongpt-hover w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left cursor-pointer ${
                          isSelected
                            ? 'bg-[#c4b5fd]/20 text-primary'
                            : 'text-secondary'
                        }`}
                      >
                        <span className="flex-1 truncate text-sm font-medium">
                          {thread.name || t('threadHistory.thread.untitled')}
                        </span>
                        <span className="text-xs text-muted flex-shrink-0">
                          {formatRelativeTime(thread.createdAt, i18n.language)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          <div className="hidden md:flex flex-1 items-center justify-center px-8">
            {selectedThread ? (
              <button
                onClick={() => openThread(selectedThread.id)}
                className="fongpt-hover w-full max-w-md text-left cursor-pointer rounded-2xl border border-outline-variant/10 bg-white/[0.03] p-6"
              >
                <p className="text-xs text-muted mb-2">
                  {formatRelativeTime(selectedThread.createdAt, i18n.language)}
                </p>
                <h3 className="text-lg font-semibold text-primary mb-2">
                  {selectedThread.name || t('threadHistory.thread.untitled')}
                </h3>
                <p className="text-sm text-secondary">
                  {t('threadHistory.sidebar.preview.open', 'Open conversation')}
                </p>
              </button>
            ) : (
              <p className="text-sm text-muted">
                {t(
                  'threadHistory.sidebar.preview.empty',
                  'Select a conversation to preview'
                )}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
